// ============================================================================
// TENANT APPLICATION ASSIGNMENT VIEW MODAL
// ============================================================================
// Read-only details view of a single tenant ↔ application assignment.
// Secrets (DB password, Keycloak client secret) are masked server-side and
// never displayed here regardless.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { TenantAppAssignment } from 'api/types';

interface Props {
  isOpen: boolean;
  record: TenantAppAssignment | null;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  marginBottom: '0.125rem',
};

const valueStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--text-primary)',
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={labelStyle}>{label}</div>
    <div style={valueStyle}>{value || <span style={{ color: 'var(--text-secondary)' }}>—</span>}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600,
                 paddingBottom: '0.375rem', borderBottom: '1px solid var(--border-color)' }}>
      {title}
    </h4>
    {children}
  </div>
);

const formatDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '');

const TenantApplicationAssignmentViewModal: React.FC<Props> = ({ isOpen, record, onClose }) => {
  if (!isOpen || !record) return null;

  const statusBadgeClass =
    record.derivedStatus === 'Active' ? 'badge-active' :
    record.derivedStatus === 'Trial' ? 'badge-warning' :
    record.derivedStatus === 'Suspended' ? 'badge-suspended' :
    'badge-inactive';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {record.tenantName} → {record.appName}
            <span className={`badge ${statusBadgeClass}`} style={{ marginLeft: '0.75rem' }}>
              {record.derivedStatus}
            </span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <Section title="Assignment">
            <div className="form-grid-2">
              <Field label="Tenant" value={`${record.tenantCode} — ${record.tenantName}`} />
              <Field label="Application" value={`${record.appCode} — ${record.appName}`} />
              <Field label="Version" value={record.tenantAppVersion ?? record.appVersion} />
              <Field label="Installed Date" value={formatDate(record.installedDate)} />
            </div>
          </Section>

          <Section title="Subscription">
            <div className="form-grid-2">
              <Field label="Plan" value={record.subscriptionPlanName} />
              <Field label="Billing Cycle" value={record.billingCycle} />
              <Field label="Subscription Start" value={formatDate(record.subscriptionStartDate)} />
              <Field label="Subscription End" value={formatDate(record.subscriptionEndDate)} />
              <Field label="Billing Email" value={record.billingEmail} />
              <Field label="Next Billing Date" value={formatDate(record.nextBillingDate)} />
              <Field label="Trial" value={record.isTrial ? 'Yes' : 'No'} />
              {record.isTrial && <Field label="Trial Ends" value={formatDate(record.trialEndDate)} />}
            </div>
          </Section>

          <Section title="Limits">
            <div className="form-grid-3">
              <Field label="Max Users" value={record.maxUsers} />
              <Field label="Storage" value={`${record.maxStorageGb} GB`} />
              <Field label="API Rate Limit" value={`${record.apiRateLimit} req/min`} />
            </div>
          </Section>

          <Section title="Keycloak Client">
            <div className="form-grid-2">
              <Field label="Client ID" value={record.keycloakClientId} />
              <Field label="Client Secret" value={<span style={{ fontFamily: 'monospace' }}>••••••••</span>} />
              <Field label="Callback URL" value={record.callbackUrl} />
              <Field label="Logout URL" value={record.logoutUrl} />
            </div>
          </Section>

          <Section title="Database">
            <div className="form-grid-3">
              <Field label="Provider" value={record.dbProvider} />
              <Field label="Host" value={record.dbHost} />
              <Field label="Port" value={record.dbPort} />
              <Field label="Database" value={record.dbName} />
              <Field label="Username" value={record.dbUsername} />
              <Field label="Password" value={<span style={{ fontFamily: 'monospace' }}>••••••••</span>} />
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <Field
                label="Connection String"
                value={record.connectionString
                  ? <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{record.connectionString}</span>
                  : undefined}
              />
            </div>
          </Section>

          <Section title="State & Health">
            <div className="form-grid-2">
              <Field label="Status" value={record.derivedStatus} />
              <Field label="Health" value={record.healthStatus} />
              <Field label="Maintenance Mode" value={record.maintenanceMode ? 'Yes' : 'No'} />
              <Field label="Suspended" value={record.isSuspended ? 'Yes' : 'No'} />
              {record.isSuspended && (
                <>
                  <Field label="Suspension Reason" value={record.suspensionReason} />
                  <Field label="Suspended At" value={formatDate(record.suspendedAt)} />
                </>
              )}
              <Field label="Last Access" value={formatDate(record.lastAccessDate)} />
            </div>
          </Section>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default TenantApplicationAssignmentViewModal;
